import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Page, snackbar, useAppLayoutContextConsumer } from '@occc-app/design-system';
import { useToggle } from '@occc/utils';
import { useBulkSelect } from '@occc-app/hooks/useBulkSelect';
import { Cpg } from '@occc/graphql';

import { ScanDataSubmissionGroupsCreate } from './scan-data-submission-groups-create.component';
import { breadcrumbLinks } from './scan-data-submission-groups-create.constants';
import { ScanDataSubmissionGroupsCreateFormValues } from './scan-data-submission-groups-create.types';
import { convertScanDataSubmissionGroupFormValuesToCreateInput } from './convert-scan-data-submission-group-form-values-to-input';

import { useCreateScanDataSftpCredentials } from '../../hooks/use-create-scan-data-sftp-credentials.hook';
import { useLocationBulkSubmitterIdUpdate } from '../../hooks/use-location-bulk-submitter-id-update.mutation.hook';
import { routeCreator } from '../../scan-data.router';

export const ScanDataSubmissionGroupsCreateContainer = () => {
  const [showCreationStatusModal, toggleCreationStatusModal] = useToggle();
  const [isEntityCreated, setIsEntityCreated] = useState(false);
  const [isErrorHappened, setIsErrorHappened] = useState(false);
  const navigate = useNavigate();
  useAppLayoutContextConsumer(breadcrumbLinks);

  const { bulkSelectedItems, handleBulkSelectMultiple, handleBulkDeselectAll, handleItemSelect } =
    useBulkSelect();

  const navigateToSubmissionGroupsPage = useCallback(() => {
    navigate(routeCreator.submissionGroups());
  }, [navigate]);

  const onCreateCompleted = useCallback(() => {
    setIsEntityCreated(true);
  }, []);

  const onCreateError = useCallback(() => {
    setIsErrorHappened(true);
  }, []);

  const handleLocationSubmitterIdUpdateSuccess = useCallback(() => {
    snackbar.success('Locations were added to Submission Group.');
  }, []);

  const handleLocationSubmitterIdUpdateError = useCallback(() => {
    snackbar.error('Unable to add Locations to Submission Group.');
  }, []);

  const [createScanDataSftpCredentials] = useCreateScanDataSftpCredentials({
    refetchQueries: [
      'getScanDataSftpCredentialsOverview',
      'getScanDataSftpCredentialsWithStoreDetailsList',
    ],
    onCompleted: onCreateCompleted,
    onError: onCreateError,
  });

  const [updateSubmitterIdForLocations] = useLocationBulkSubmitterIdUpdate({
    refetchQueries: ['getScanDataSftpCredentialsWithStoreDetailsList'],
    onCompleted: handleLocationSubmitterIdUpdateSuccess,
    onError: handleLocationSubmitterIdUpdateError,
  });

  const onSubmit = useCallback(
    async (formValues: ScanDataSubmissionGroupsCreateFormValues) => {
      toggleCreationStatusModal();
      setIsErrorHappened(false);

      await createScanDataSftpCredentials({
        variables: { input: convertScanDataSubmissionGroupFormValuesToCreateInput(formValues) },
      });

      if (bulkSelectedItems.size === 0) {
        return;
      }

      const selectedLocationUuids = Array.from(bulkSelectedItems);

      const submitterID = formValues.sftpDetails.submitterID;
      const { tobaccoCompany } = formValues;

      await updateSubmitterIdForLocations({
        variables: {
          input: {
            uuids: selectedLocationUuids,
            altriaSubmitterID: tobaccoCompany === Cpg.Altria ? submitterID : undefined,
            circanaSubmitterID: tobaccoCompany === Cpg.Circana ? submitterID : undefined,
            msaSubmitterID: tobaccoCompany === Cpg.Msa ? submitterID : undefined,
          },
        },
      });
    },
    [
      createScanDataSftpCredentials,
      toggleCreationStatusModal,
      updateSubmitterIdForLocations,
      bulkSelectedItems,
    ],
  );

  return (
    <Page
      pageHeaderProps={{
        title: 'New Submission Group',
        subtitle: 'Maximize savings with discounts on your purchase today',
      }}
      browserTabTitle='Create Submission Group'
    >
      <ScanDataSubmissionGroupsCreate
        showCreationStatusModal={showCreationStatusModal}
        onCreationStatusModalHide={toggleCreationStatusModal}
        onCreationStatusModalOk={navigateToSubmissionGroupsPage}
        isEntityCreated={isEntityCreated}
        isErrorHappened={isErrorHappened}
        onSubmit={onSubmit}
        onCancel={navigateToSubmissionGroupsPage}
        bulkSelectedItems={bulkSelectedItems}
        handleBulkSelectMultiple={handleBulkSelectMultiple}
        handleBulkDeselectAll={handleBulkDeselectAll}
        handleItemSelect={handleItemSelect}
      />
    </Page>
  );
};
